import { useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router';

import BrandLogo from '@/components/BrandLogo';
import {
  PublicPracticeApiError,
  usePublicPracticeArtist,
} from '@/features/public-practice/api';
import { parsePublicPracticeArtistBootstrap } from '@/features/public-practice/catalog-bootstrap';
import PublicPracticeSongList from '@/features/public-practice/PublicPracticeSongList';

import '../styles/public-practice.css';

const NOT_FOUND_TITLE = '페이지를 찾을 수 없습니다 · 지음';

export default function PublicPracticeArtistRoute() {
  const { locale = '', artistSlug = '' } = useParams<{
    locale: string;
    artistSlug: string;
  }>();
  const location = useLocation();

  return (
    <PublicPracticeArtistResource
      key={`${locale}:${artistSlug}:${location.pathname}`}
      artistSlug={artistSlug}
      locale={locale}
      pathname={location.pathname}
    />
  );
}

function PublicPracticeArtistResource({
  artistSlug,
  locale,
  pathname,
}: {
  artistSlug: string;
  locale: string;
  pathname: string;
}) {
  const initialArtist = useMemo(
    () => parsePublicPracticeArtistBootstrap({
      locale,
      artistSlug,
      pathname,
    }),
    [artistSlug, locale, pathname],
  );
  const query = usePublicPracticeArtist(
    locale === 'ko' ? artistSlug : null,
    initialArtist,
    { enabled: locale === 'ko' },
  );
  const notFound = (
    locale !== 'ko'
    || (
      query.error instanceof PublicPracticeApiError
      && query.error.status === 404
    )
  );
  const title = query.data
    ? `${query.data.name} 보컬 연습곡 · MR과 연습실 | 지음`
    : '아티스트 보컬 연습곡 · 지음';

  useEffect(() => {
    document.title = notFound ? NOT_FOUND_TITLE : title;
  }, [notFound, title]);

  if (notFound) {
    return (
      <div className="route-shell public-practice-route-state">
        404 — 공개 아티스트 연습곡을 찾을 수 없습니다.
      </div>
    );
  }

  if (!query.data) {
    return (
      <div className="route-shell public-practice-route-state">
        {query.isPending
          ? '아티스트 연습곡을 불러오는 중입니다.'
          : '아티스트 연습곡을 불러오지 못했습니다. 잠시 뒤 다시 확인해 주세요.'}
      </div>
    );
  }

  return (
    <main
      className="public-practice-artist-page"
      data-route="public-practice-artist"
      lang="ko"
    >
      <nav className="public-practice-breadcrumbs" aria-label="현재 위치">
        <Link className="brand-link" to="/ko" aria-label="지음">
          <BrandLogo language="ko" decorative />
        </Link>
        <span aria-hidden="true">/</span>
        <Link to="/ko/practice-songs">공개 연습곡</Link>
        <span aria-hidden="true">/</span>
        <span>{query.data.name}</span>
      </nav>

      <header className="public-practice-artist-hero">
        <p>아티스트별 연습곡</p>
        <h1>{query.data.name} 보컬 연습곡</h1>
        <p>
          현재 공개된 곡을 보컬 음역과 난이도,
          {' '}연습 버전 정보와 함께 모았습니다.
        </p>
      </header>

      {query.isError ? (
        <p className="public-practice-index-notice" role="status">
          목록을 새로 확인하지 못했습니다. 현재 내용을 계속 보여드립니다.
        </p>
      ) : null}

      <PublicPracticeSongList songs={query.data.songs} />
    </main>
  );
}
